import { useEffect } from 'react';

import { AuthDenialCode } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/ui/toaster';
import { useAuthDenialStore } from '@/stores/auth-denial.store';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceSwitcherStore } from '@/stores/workspace-switcher-store';

import SwitchIcon from '../icons/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

/** Denials answered by sending the user somewhere else instead of explaining in place. */
const DENIAL_ROUTE = {
  [AuthDenialCode.NoGearbeltRoleAnywhere]: '/no-workspace-available',
} as const;

type RoutedDenialCode = keyof typeof DENIAL_ROUTE;
type ExplainedDenialCode = Exclude<AuthDenialCode, RoutedDenialCode>;

const COPY: Record<ExplainedDenialCode, { title: string; body: string; action?: 'switch-workspace' | 'retry' }> = {
  [AuthDenialCode.WorkspaceAccessDenied]: {
    title: "You don't have access to this workspace",
    body: 'Your account is not set up for this workspace yet. Switch to one you have access to, or ask an admin to invite you.',
    action: 'switch-workspace',
  },
  [AuthDenialCode.NoEmailOnIdentity]: {
    title: 'This account cannot use Gearbelt on the web',
    body: 'Your Sitemate account has no email address, which Gearbelt needs to sign you in on desktop. Contact your administrator to add one.',
  },
  [AuthDenialCode.PermissionLookupFailed]: {
    title: "We couldn't check your access",
    body: "Something went wrong confirming your permissions, so we've left you signed in rather than guessing. This is usually temporary — try again in a moment.",
    action: 'retry',
  },
};

function isRoutedDenial(code: AuthDenialCode): code is RoutedDenialCode {
  return code in DENIAL_ROUTE;
}

/**
 * Mounted in `AppBootstrap` rather than a layout: a denial can fire before any
 * layout renders, including from the bootstrap's own queries.
 */
export function AuthDenialModal() {
  const deniedReason = useAuthDenialStore((state) => state.deniedReason);
  const setDeniedReason = useAuthDenialStore((state) => state.setDeniedReason);
  const markNoGearbeltRoleAnywhere = useAuthDenialStore((state) => state.markNoGearbeltRoleAnywhere);
  const setIsSwitcherOpen = useWorkspaceSwitcherStore((state) => state.setIsOpen);
  const { setUser, setOrg } = useUserStore();
  const { signoutRedirect } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!deniedReason || !isRoutedDenial(deniedReason)) return;

    if (deniedReason === AuthDenialCode.NoGearbeltRoleAnywhere) {
      markNoGearbeltRoleAnywhere();
    }

    // Clearing `org` is load-bearing, not tidiness: the workspace we're holding
    // is one the user can't use, and `/no-workspace-available` bounces straight
    // back to `/dashboard` while it's still set.
    setDeniedReason(null);
    setOrg(null);
    navigate(DENIAL_ROUTE[deniedReason]);
  }, [deniedReason, setDeniedReason, markNoGearbeltRoleAnywhere, setOrg, navigate]);

  if (!deniedReason || isRoutedDenial(deniedReason)) {
    return null;
  }

  const { title, body, action } = COPY[deniedReason];

  return (
    <Dialog open>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle hideCloseButton={true} className="text-default-text flex justify-center border-b-0 text-xl leading-normal font-medium">
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-default-text flex flex-col items-center gap-4 leading-normal font-normal">
          <span className="text-center">{body}</span>

          {action === 'switch-workspace' && (
            <Button
              variant="grey"
              onClick={() => {
                setDeniedReason(null);
                setIsSwitcherOpen(true);
              }}>
              <SwitchIcon size={20} className="pr-1" />
              Switch workspace
            </Button>
          )}

          {action === 'retry' && (
            <Button variant="grey" onClick={() => window.location.reload()}>
              Try again
            </Button>
          )}

          <Button
            variant="link"
            className="p-0 underline"
            onClick={async () => {
              setDeniedReason(null);
              setUser(null);
              setOrg(null);

              await signoutRedirect({
                post_logout_redirect_uri: window.location.origin,
              }).catch(() => toast.error('Your request could not be completed. Please try again.'));
            }}>
            Log out
          </Button>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
}
